import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { fetchBracket, fetchPlayerPredictions } from '@/api/bracket'
import { useSessionStore } from '@/store/sessionStore'
import { useLocalizedTeamName } from '@/utils/teamNames'
import { Spinner } from '@/components/ui/Spinner'
import type { Bracket, Prediction, Team } from '@/types/bracket'
import type { Round } from '@/types/bracket'

const ROUND_ORDER: Round[] = ['R32', 'R16', 'QF', 'SF', '3RD', 'FINAL']

interface Props {
  playerId: string
  displayName: string
  onClose: () => void
}

export function PlayerPicksModal({ playerId, displayName, onClose }: Props) {
  const { t } = useTranslation()
  const { token } = useSessionStore()
  const getTeamName = useLocalizedTeamName()
  const [bracket, setBracket] = useState<Bracket | null>(null)
  const [preds, setPreds] = useState<Prediction[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [teamsMap, setTeamsMap] = useState<Map<string, Team>>(new Map())

  useEffect(() => {
    async function load() {
      try {
        const [b, p] = await Promise.all([
          fetchBracket(token ?? undefined),
          fetchPlayerPredictions(playerId, token ?? undefined),
        ])
        setBracket(b)
        setPreds(p)
        // Build a full team map from all matches that have real teams
        const tm = new Map<string, Team>()
        for (const round of b.rounds) {
          for (const m of round.matches) {
            if (m.home_team) tm.set(m.home_team.id, m.home_team)
            if (m.away_team) tm.set(m.away_team.id, m.away_team)
            if (m.winner_team) tm.set(m.winner_team.id, m.winner_team)
          }
        }
        setTeamsMap(tm)
      } catch {
        setError('Could not load picks.')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [playerId, token])

  // Build lookup maps
  const predByMatch = new Map(preds.map(p => [p.match_id, p]))

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="bg-pitch-900 border border-white/10 rounded-2xl w-full max-w-lg max-h-[85vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 flex-shrink-0">
          <div>
            <h2 className="font-black text-white text-lg">{displayName}</h2>
            <p className="text-xs text-white/40">{t('leaderboard.picksTitle')}</p>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white text-2xl leading-none">×</button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-5">
          {loading && (
            <div className="flex justify-center py-12"><Spinner size="lg" /></div>
          )}
          {error && (
            <p className="text-center text-white/40 py-12">{error}</p>
          )}
          {!loading && !error && preds.length === 0 && (
            <p className="text-center text-white/40 py-12">{t('leaderboard.picksLocked')}</p>
          )}
          {!loading && !error && bracket && preds.length > 0 && (
            ROUND_ORDER.map(round => {
              const roundMatches = bracket.rounds.find(r => r.round === round)?.matches ?? []
              const roundPreds = roundMatches.filter(m => predByMatch.has(m.id))
              if (roundPreds.length === 0) return null

              return (
                <div key={round}>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-white/30 mb-2">
                    {t(`bracket.rounds.${round}`)}
                  </div>
                  <div className="space-y-1.5">
                    {roundPreds.map(m => {
                      const pred = predByMatch.get(m.id)!
                      const pickedId = pred.predicted_winner_id
                      const pickedTeam = teamsMap.get(pickedId)
                      const homeTeam = m.home_team
                      const awayTeam = m.away_team
                      const teamsKnown = !!(homeTeam && awayTeam)
                      const pickedHome = pickedId === m.home_team_id
                      const actualWinner = m.winner_team_id
                      const isCorrect = actualWinner && actualWinner === pickedId
                      const isWrong = actualWinner && actualWinner !== pickedId
                      const hasScore = pred.predicted_home_score != null && pred.predicted_away_score != null

                      const teamLabel = (t: Team | null | undefined) =>
                        t ? `${t.flag_emoji ?? ''} ${getTeamName(t.id, t.name)}` : 'TBD'

                      return (
                        <div
                          key={m.id}
                          className={`rounded-lg px-3 py-2 text-sm flex items-center gap-2
                            ${isCorrect ? 'bg-emerald-500/10 border border-emerald-500/20' : ''}
                            ${isWrong ? 'bg-red-500/5 border border-red-500/10' : ''}
                            ${!actualWinner ? 'bg-white/[0.04] border border-white/5' : ''}
                          `}
                        >
                          <div className="flex-1 min-w-0">
                            {teamsKnown ? (
                              /* Teams are set — show home vs away with pick highlighted */
                              <div className="flex items-center gap-1.5">
                                <span className={`truncate ${pickedHome ? 'font-bold text-white' : 'text-white/50'}`}>
                                  {teamLabel(homeTeam)}
                                </span>
                                <span className="text-white/20 text-xs flex-shrink-0">vs</span>
                                <span className={`truncate ${!pickedHome ? 'font-bold text-white' : 'text-white/50'}`}>
                                  {teamLabel(awayTeam)}
                                </span>
                              </div>
                            ) : (
                              /* Teams TBD — just show who they picked */
                              <div className="flex items-center gap-1.5">
                                <span className="text-white/30 text-xs">Picked:</span>
                                <span className="font-bold text-white">
                                  {pickedTeam ? teamLabel(pickedTeam) : pickedId}
                                </span>
                                <span className="text-white/20 text-xs">· opponent TBD</span>
                              </div>
                            )}
                            {hasScore && (
                              <div className="text-[10px] text-white/30 mt-0.5">
                                Score: {pred.predicted_home_score}–{pred.predicted_away_score}
                              </div>
                            )}
                          </div>

                          {/* Result indicator */}
                          {isCorrect && <span className="text-emerald-400 text-base flex-shrink-0">✓</span>}
                          {isWrong && <span className="text-red-400 text-base flex-shrink-0">✗</span>}
                          {!actualWinner && <span className="text-white/20 text-xs flex-shrink-0">pending</span>}
                        </div>
                      )
                    })}
                  </div>
                </div>
              )
            })
          )}
        </div>
      </div>
    </div>
  )
}
